<template>
  <BaseWidget 
    title="Manual vs. automated actions"
    :show-info-icon="true"
    info-tooltip="Comparison of actions performed manually versus those handled by automations."
    :info-tooltip-link="{ url: 'https://support.optixapp.com/en/articles/9500741-what-is-an-automation', text: 'Learn about Automations' }"
    :edit-mode="editMode"
    :widget-id="props.widgetId"
    :row-span="rowSpan"
    :col-span="colSpan"
    :date-range="dateRange"
    :past-only="true"
    @update:date-range="handleDateRangeChange"
    @remove-widget="$emit('remove-widget', props.widgetId)"
  >
    <div
      v-if="loading"
      class="loading-state"
    >
      <SkeletonLoader
        type="widget"
        :compact="focused"
      />
    </div>
    <div
      v-else-if="error"
      class="error-state"
    >
      <RateLimitError v-if="isRateLimitError(error)" />
      <span v-else>Error: {{ error.message }}</span>
    </div>
    <div
      v-else
      class="widget-content-wrapper"
    >
      <div class="chart-container">
        <div class="date-range-subtitle">
          {{ dateRangeLabel }}
        </div>
        <div class="chart-area">
          <!-- Chart content -->
          <div class="chart-content">
            <div class="bars-container">
              <div 
                v-for="(action, index) in actionsData" 
                :key="action.name"
                class="bar-group"
              >
                <div class="bars-pair">
                  <!-- Manual bar -->
                  <div class="bar-wrapper">
                    <div 
                      class="bar manual-bar"
                      :style="{ 
                        height: `${getBarHeight(action.manual)}%`,
                      }"
                    >
                      <div class="tooltip">
                        <span class="tooltip-label">Manual</span>
                        <span class="tooltip-value">{{ action.manual }}</span>
                      </div>
                    </div>
                  </div>
                  <!-- Automated bar -->
                  <div class="bar-wrapper">
                    <div 
                      class="bar automated-bar"
                      :style="{ 
                        height: `${getBarHeight(action.automated)}%`,
                      }"
                    >
                      <div class="tooltip">
                        <span class="tooltip-label">Automated</span>
                        <span class="tooltip-value">{{ action.automated }}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="action-label">
                  {{ action.name }}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Legend -->
        <div class="legend">
          <div class="legend-item">
            <div class="legend-color manual" />
            <span>Manual</span>
          </div>
          <div class="legend-item">
            <div class="legend-color automated" />
            <span>Automated</span>
          </div>
        </div>
        
        <div
          v-if="actionsData.length === 0"
          class="no-data"
        >
          No data available
        </div>
      </div>
    </div>
  </BaseWidget>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import BaseWidget from './BaseWidget.vue'
import SkeletonLoader from './SkeletonLoader.vue'
import RateLimitError from './RateLimitError.vue'
import { isRateLimitError } from '../utils/errorUtils.js'
import { DATE_RANGE_LABELS } from '../utils/dateRanges.js'

const props = defineProps({
  rowSpan: {
    type: Number,
    default: 1
  },
  colSpan: {
    type: Number,
    default: 1
  },
  editMode: {
    type: Boolean,
    default: false
  },
  props: {
    type: Object,
    default: () => ({})
  },
  widgetId: {
    type: String,
    default: null
  },
  sectionId: {
    type: String,
    default: null
  },
  widgetType: {
    type: String,
    default: null
  },
  focused: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:props', 'remove-widget'])

const loading = ref(false)
const error = ref(null)

// Initialize dateRange from props, fallback to default
const dateRange = ref(props.props?.dateRange || 'last_30_days')

const dateRangeLabel = computed(() => {
  return DATE_RANGE_LABELS[dateRange.value] || 'Last 30 days'
})

// Fake data for now
const actionsData = ref([
  { name: 'Message sent', manual: 83, automated: 25 },
  { name: 'Add invoice item', manual: 42, automated: 67 },
  { name: 'Create booking', manual: 15, automated: 92 },
  { name: 'Update user', manual: 58, automated: 34 },
  { name: 'Send reminder', manual: 12, automated: 88 }
])

// Calculate max value for scaling bars (for vertical chart)
const maxValue = computed(() => {
  if (actionsData.value.length === 0) return 1
  const allValues = actionsData.value.flatMap(a => [a.manual, a.automated])
  return Math.max(...allValues, 1)
})

// Calculate totals
const totalManual = computed(() => {
  return actionsData.value.reduce((sum, action) => sum + action.manual, 0)
})

const totalAutomated = computed(() => {
  return actionsData.value.reduce((sum, action) => sum + action.automated, 0)
})

// Get bar height percentage
const getBarHeight = (value) => {
  if (maxValue.value === 0) return 0
  return (value / maxValue.value) * 100
}

const handleDateRangeChange = (newDateRange) => {
  if (newDateRange !== dateRange.value) {
    dateRange.value = newDateRange
    emit('update:props', { dateRange: newDateRange })
  }
}

// Watch for prop changes from parent (e.g., when loading from database)
watch(() => props.props?.dateRange, (newDateRange) => {
  if (newDateRange && newDateRange !== dateRange.value) {
    dateRange.value = newDateRange
  }
}, { immediate: true })
</script>

<style scoped>
.widget-content-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 100%;
  min-height: 0;
  overflow: hidden;
}

.loading-state,
.error-state {
  padding: 20px;
  text-align: center;
  color: rgba(0, 0, 0, 0.87);
  font-size: 14px;
}

.error-state {
  color: var(--color-red);
}

.no-data {
  padding: 20px;
  text-align: center;
  color: #999;
  font-size: 14px;
}

.date-range-subtitle {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.87);
  margin-bottom: 12px;
}

.chart-container {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  padding: 8px 0;
  overflow: hidden;
  max-height: 100%;
}

.chart-area {
  display: flex;
  flex: 1;
  min-height: 0;
  max-height: 100%;
  position: relative;
  overflow: hidden;
}

.chart-content {
  flex: 1;
  display: flex;
  min-height: 0;
  max-height: 100%;
  position: relative;
  overflow: hidden;
}

.bars-container {
  display: flex;
  align-items: flex-end;
  justify-content: space-around;
  gap: 12px;
  flex: 1;
  padding: 0 8px;
  min-height: 0;
  max-height: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
}

.bar-group {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  flex: 1;
  max-width: 120px;
  height: 100%;
  max-height: 100%;
  min-height: 0;
  overflow: hidden;
}

.bars-pair {
  display: flex;
  gap: 0px;
  align-items: flex-end;
  width: 100%;
  justify-content: center;
  height: 100%;
  max-height: 100%;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.bar-wrapper {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  min-width: 0;
  height: 100%;
  max-height: 100%;
  position: relative;
  overflow: hidden;
}

.bar {
  width: 100%;
  min-width: 12px;
  max-width: 20px;
  border-radius: 4px 4px 0 0;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  transition: height 0.3s ease;
  position: relative;
  padding-top: 4px;
  min-height: 0;
  max-height: 100%;
  box-sizing: border-box;
  overflow: hidden;
}

.manual-bar {
  background-color: #D3E5EF;
}

.automated-bar {
  background-color: #337EA9;
}

.tooltip {
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: 8px;
  padding: 6px 10px;
  background-color: rgba(0, 0, 0, 0.85);
  color: white;
  border-radius: 4px;
  font-size: 12px;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;
  z-index: 10;
  display: flex;
  gap: 8px;
  align-items: center;
}

.bar:hover .tooltip {
  opacity: 1;
}

.tooltip-label {
  font-weight: 500;
}

.tooltip-value {
  font-weight: 600;
}

.action-label {
  font-size: 11px;
  color: rgba(0, 0, 0, 0.87);
  font-weight: 500;
  text-align: center;
  word-break: break-word;
  line-height: 1.2;
  max-width: 100%;
  flex-shrink: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.legend {
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e5e7eb;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.87);
}

.legend-color {
  width: 12px;
  height: 12px;
  border-radius: 2px;
}

.legend-color.manual {
  background-color: #D3E5EF;
}

.legend-color.automated {
  background-color: #337EA9;
}

/* Compact mode adjustments */
@media (max-width: 600px) {
  .bars-pair {
    gap: 3px;
  }
  
  .bar {
    min-width: 20px;
    max-width: 40px;
  }
  
  .bar-value {
    font-size: 10px;
  }
  
  .action-label {
    font-size: 10px;
  }
  
  .y-axis-label {
    font-size: 10px;
  }
}
</style>
